import { useRef, useEffect, useState, useCallback } from 'react';
import {
  ActionSheetChartContainer,
  ActionSheetTableContainer,
  ActionSheetTabContainer,
  TableHeaderBackground,
  DataTableContainer,
  TableOverflowWrapper,
  TableOverflowEdges,
  TableOverflowShadow,
  NoMoreCompanies,
} from './styled';
import MultipleVsIndustryChartContainer from '../../../MultipleVsIndustryChart/MultipleVsIndustryChartContainer';
import DataTable from '../DataTable';
import TabSelection from '../TabSelection';
import { useIndustryCompanies } from '../../../../hooks/useIndustryCompanies';
import { useIndustryContext } from '../../../IndustryContextProvider';

import { useValuationData } from '@/pages/CompanyReport/components/Valuation';

interface Props {
  numberOfCompanies: number;
  companiesIsLoading?: boolean;
  isCompanyInCurrentRange?: boolean;
  industry: {
    name: string;
    ratioFormatted: string;
    salesGrowthFormatted: string;
    earningsGrowthFormatted: string;
  };
}

const ActionSheetTable = ({
  numberOfCompanies,
  companiesIsLoading,
  isCompanyInCurrentRange,
  industry,
}: Props) => {
  //For shadow
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const topOverflowRef = useRef<HTMLDivElement>(null);
  const bottomOverflowRef = useRef<HTMLDivElement>(null);
  const [showTopShadow, setShowTopShadow] = useState<boolean>(false);
  const [showBottomShadow, setShowBottomShadow] = useState<boolean>(false);
  const { index, multiple } = useIndustryContext();
  useEffect(() => {
    const handleIntersect = (e: IntersectionObserverEntry[]) => {
      e.forEach((event) => {
        const setter =
          event.target.id === 'top-overflow-shadow'
            ? setShowTopShadow
            : setShowBottomShadow;
        setter(event.isIntersecting !== true);
      });
    };

    const observer = new IntersectionObserver(handleIntersect, {
      root: tableContainerRef.current,
    });
    if (topOverflowRef.current && bottomOverflowRef.current) {
      observer.observe(topOverflowRef.current);
      observer.observe(bottomOverflowRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const stopPropagation = useCallback(
    (e: { stopPropagation: () => void }) => e.stopPropagation(),
    []
  );

  //Get info about selected company from API
  const { multiples, tickerSymbol, uniqueSymbol, canonicalUrl } =
    useValuationData();
  const { companies, company, industryCompanies } = useIndustryCompanies({
    tickerSymbol,
    index,
    limit: numberOfCompanies,
    selectedMultiple: multiple,
    uniqueSymbol,
    canonicalUrl,
    multiples,
  });

  return (
    <>
      <ActionSheetChartContainer>
        <MultipleVsIndustryChartContainer
          height={228}
          heightSm={168}
          multiples={multiples}
          tickerSymbol={tickerSymbol}
          uniqueSymbol={uniqueSymbol}
          canonicalUrl={canonicalUrl}
        />
      </ActionSheetChartContainer>
      <ActionSheetTabContainer>
        <TabSelection industryCompanies={industryCompanies} />
      </ActionSheetTabContainer>
      <ActionSheetTableContainer ref={tableContainerRef}>
        <TableHeaderBackground expanded={isCompanyInCurrentRange} />
        {/* This is needed to stop it from intefering with Actionsheet */}
        <DataTableContainer
          onMouseDown={stopPropagation}
          onMouseMove={stopPropagation}
          onMouseUp={stopPropagation}
          onTouchStart={stopPropagation}
          onTouchMove={stopPropagation}
          onTouchEnd={stopPropagation}
        >
          <TableOverflowWrapper>
            <TableOverflowEdges
              id="top-overflow-shadow"
              ref={topOverflowRef}
              side="top"
              expanded={isCompanyInCurrentRange}
            />
            <TableOverflowEdges
              id="bottom-overflow-shadow"
              ref={bottomOverflowRef}
              side="bottom"
              expanded={isCompanyInCurrentRange}
            />
            <DataTable
              data-cy-id={'valuation-multiple-vs-industry-table-actionsheet'}
              company={company}
              companies={companies || []}
              numberOfCompanies={numberOfCompanies}
              companiesIsLoading={companiesIsLoading}
              companiesToShow={numberOfCompanies}
              preferredMultiple={multiple}
              pinHighlighted
              industry={industry}
              isCompanyInCurrentRange={isCompanyInCurrentRange}
            />
            <NoMoreCompanies>No more companies</NoMoreCompanies>
          </TableOverflowWrapper>
        </DataTableContainer>
        {showTopShadow && (
          <TableOverflowShadow side="top" expanded={isCompanyInCurrentRange} />
        )}
        {showBottomShadow && (
          <TableOverflowShadow
            side="bottom"
            expanded={isCompanyInCurrentRange}
          />
        )}
      </ActionSheetTableContainer>
    </>
  );
};

export default ActionSheetTable;
